"use client";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { createWork, getWork } from "@/actions/work";
import { WorkAccessLevelBadgeDropdown } from "@/components/dashboard/forms/work-access-level-badge-dropdown";
import ComboboxFormField from "@/components/form-elements/combobox-form-field";
import { ProjectFunctionAssignmentField } from "@/components/project/project-function-assignment-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { queryKeys } from "@/constants/query";
import useOrgSettings from "@/hooks/use-org-settings";
import { getPeopleOptions, usePeople } from "@/hooks/use-people";
import { useWorkAccessLevels } from "@/hooks/useWorkAccessLevels";
import {
  WorkFormSchema,
  type WorkFormValues,
} from "@/schemas/work-form-schema";
import type { AssignableFunctionFacet } from "@/types/project-product-context";

import {
  DatePickerField,
  DocumentationField,
  NumberField,
  SelectField,
  TextareaField,
  TextInputField,
} from "./work-form-fields";
import {
  createWorkModelFromFormValues,
  getDefaultFormValues,
  useWorkFormData,
} from "./work-form-utils";

interface CreateWorkFormProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkCreated: () => void;
  parentId?: string | null;
  parentName?: string | null;
  /**
   * When the sidebar product filter is a specific product, narrow the optional
   * function picker to that product (does not auto-link).
   */
  preferredProductRootId?: string | null;
  // Optional initial start date to prefill the form when opened from calendar
  initialStartDate?: Date | null;
}

export function CreateWorkForm({
  orgId,
  open,
  onOpenChange,
  onWorkCreated,
  parentId = null,
  parentName = null,
  preferredProductRootId = null,
  initialStartDate = null,
}: CreateWorkFormProps) {
  const settings = useOrgSettings();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { projectId } = useParams();
  const isNewProject = !parentId;

  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [titleActions, setTitleActions] = useState<React.ReactNode>(null);
  const [functionFacetId, setFunctionFacetId] = useState<string | null>(null);
  const [linkedFunctionOption, setLinkedFunctionOption] =
    useState<AssignableFunctionFacet | null>(null);
  const {
    workTypes,
    estimateTypes,
    workStatuses,
    isWorkTypesLoading,
    isEstimateTypesLoading,
    isWorkStatusesLoading,
  } = useWorkFormData();
  const { workAccessLevels, isLoading: isWorkAccessLevelsLoading } =
    useWorkAccessLevels();
  const { people } = usePeople();

  const form = useForm<WorkFormValues>({
    resolver: zodResolver(WorkFormSchema),
    defaultValues: {
      ...getDefaultFormValues(orgId, parentId),
      tentativeStartDate: initialStartDate ?? new Date(),
      tentativeEndDate: initialStartDate ?? new Date(),
    },
  });

  // Compute default workTypeId, statusId, and accessLevelId on form open/load
  useEffect(() => {
    if (!open) return;
    if (
      isWorkTypesLoading ||
      isWorkStatusesLoading ||
      isEstimateTypesLoading ||
      isWorkAccessLevelsLoading
    )
      return;

    const applyDefaults = async () => {
      let updated = false;

      // Default workTypeId by hierarchy
      const currentWorkType = form.getValues("workTypeId");
      if (!currentWorkType) {
        const findTypeIdByName = (name: string) =>
          (workTypes || []).find(
            (wt) => (wt.name || "").trim().toLowerCase() === name.toLowerCase(),
          )?.id || null;

        let desiredTypeId: string | null = null;
        if (!parentId) {
          // No parent: Project
          desiredTypeId = findTypeIdByName("Project");
        } else {
          try {
            const parent = await getWork(parentId);
            const parentTypeName = (workTypes || [])
              .find((wt) => wt.id === parent?.workTypeId)
              ?.name?.trim()
              .toLowerCase();
            if (parentTypeName === "project")
              desiredTypeId = findTypeIdByName("Phase");
            else if (parentTypeName === "phase")
              desiredTypeId = findTypeIdByName("Task");
            else desiredTypeId = findTypeIdByName("Task");
          } catch {
            desiredTypeId = findTypeIdByName("Task");
          }
        }

        if (desiredTypeId) {
          form.setValue("workTypeId", desiredTypeId, { shouldDirty: false });
          updated = true;
        }
      }

      // Default statusId to Open
      const currentStatus = form.getValues("statusId");
      if (!currentStatus) {
        const openStatusId =
          (workStatuses || []).find(
            (s) => (s.name || "").trim().toLowerCase() === "open",
          )?.id || null;
        if (openStatusId) {
          form.setValue("statusId", openStatusId, { shouldDirty: false });
          updated = true;
        }
      }

      // Default accessLevelId to Private for new projects (no parentId)
      if (!parentId) {
        const currentAccessLevelId = form.getValues("accessLevelId");
        if (!currentAccessLevelId && workAccessLevels) {
          const privateAccessLevelId =
            workAccessLevels.find(
              (level) => level.name?.toLowerCase() === "private",
            )?.id || null;

          form.setValue("accessLevelId", privateAccessLevelId, {
            shouldDirty: false,
          });
          updated = true;
        }
      }

      if (updated) {
        // Optionally trigger validation for these fields
        form.trigger(["workTypeId", "statusId", "accessLevelId"]);
      }
    };

    void applyDefaults();
  }, [
    open,
    parentId,
    isWorkTypesLoading,
    isWorkStatusesLoading,
    isEstimateTypesLoading,
    isWorkAccessLevelsLoading,
    workTypes?.length,
    workStatuses?.length,
    estimateTypes?.length,
    workAccessLevels?.length,
    form,
  ]);

  // Handle dialog open/close
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // If closing: prompt only when there are unsaved changes
      if (form.formState.isDirty) {
        setShowUnsavedChangesDialog(true);
        return;
      }

      // No unsaved changes: close immediately and reset
      onOpenChange(false);
      form.reset();
      setFunctionFacetId(null);
      setLinkedFunctionOption(null);
      return;
    }

    // Opening the dialog
    onOpenChange(true);
  };

  // Initialize or update tentativeStartDate when dialog opens or initialStartDate changes
  useEffect(() => {
    if (!open) return;

    const currentAccessLevelId = form.getValues("accessLevelId");
    const defaults = {
      ...getDefaultFormValues(orgId, parentId),
      tentativeStartDate: initialStartDate ?? new Date(),
      tentativeEndDate: initialStartDate ?? new Date(),
      // Preserve accessLevelId if it was already set
      accessLevelId: currentAccessLevelId || null,
    } as WorkFormValues;

    const currentStart = form.getValues("tentativeStartDate");
    const currentEnd = form.getValues("tentativeEndDate");
    const currentStartTime = (currentStart as Date | null)?.getTime?.() ?? null;
    const currentEndTime = (currentEnd as Date | null)?.getTime?.() ?? null;
    const defaultsStartTime =
      (defaults.tentativeStartDate as Date | null)?.getTime?.() ?? null;
    const defaultsEndTime =
      (defaults.tentativeEndDate as Date | null)?.getTime?.() ?? null;

    if (
      currentStartTime !== defaultsStartTime ||
      currentEndTime !== defaultsEndTime
    ) {
      form.reset(defaults);
    }
  }, [open, initialStartDate, orgId, parentId, form]);

  // Set title actions for projects (access level dropdown)
  useEffect(() => {
    if (!open) {
      setTitleActions(null);
      return;
    }

    // Only show for projects (no parentId)
    if (parentId) {
      setTitleActions(null);
      return;
    }

    // Wait for access levels to load
    if (isWorkAccessLevelsLoading || !workAccessLevels) {
      return;
    }

    const currentAccessLevelId = form.getValues("accessLevelId");
    const privateAccessLevelId =
      workAccessLevels.find((level) => level.name?.toLowerCase() === "private")
        ?.id || null;

    // Use current value or default to Private
    const displayAccessLevelId = currentAccessLevelId || privateAccessLevelId;

    setTitleActions(
      <WorkAccessLevelBadgeDropdown
        value={displayAccessLevelId}
        options={workAccessLevels}
        onValueChange={(value) => {
          form.setValue("accessLevelId", value, {
            shouldDirty: true,
          });
        }}
        disabled={isWorkAccessLevelsLoading}
      />,
    );
  }, [open, parentId, isWorkAccessLevelsLoading, workAccessLevels, form]);

  // Handle confirmation dialog cancel
  const handleCancelClose = () => {
    setShowUnsavedChangesDialog(false);
  };

  // Handle confirmation dialog confirm
  const handleConfirmClose = () => {
    form.reset();
    setFunctionFacetId(null);
    setLinkedFunctionOption(null);
    setShowUnsavedChangesDialog(false);
    onOpenChange(false);
  };

  // Handle form submission
  const onSubmit = async (values: WorkFormValues) => {
    try {
      // Ensure accessLevelId is included in the values
      const formValuesWithAccessLevel: WorkFormValues = {
        ...values,
        accessLevelId:
          values.accessLevelId || form.getValues("accessLevelId") || null,
      };

      const workModel = await createWorkModelFromFormValues(
        formValuesWithAccessLevel,
        orgId,
        parentId,
        projectId as string,
        undefined,
        functionFacetId,
      );

      const createdWork = await createWork(workModel);

      // Invalidate queries BEFORE closing dialog and redirecting
      // Only invalidate project-specific queries if we have a projectId (work item under a project)
      if (projectId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.getWorkTreeById(projectId as string),
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.getProjectItem(projectId as string),
        });
      }

      // Always invalidate org-level project lists (for sidebar)
      // Only invalidate masterWorkItems if creating a project (no parentId)
      if (!parentId) {
        await queryClient.invalidateQueries({
          queryKey: ["masterWorkItems", orgId],
        });
        if (linkedFunctionOption?.productRootId) {
          const productRootId = linkedFunctionOption.productRootId;
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.linkedFacetIdsForProduct(
                orgId,
                productRootId,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.productProjectsExecution(
                orgId,
                productRootId,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.productsHubSummaries(orgId),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.productIntelligence(orgId, productRootId),
            }),
          ]);
        }
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectNavItems(orgId),
      });

      // Show success toast
      toast.success(
        isNewProject
          ? functionFacetId
            ? "Project created and linked to product"
            : "Project created successfully"
          : "Work item created successfully",
        { duration: 3000 },
      );

      // Close dialog
      onOpenChange(false);
      form.reset();
      setFunctionFacetId(null);
      setLinkedFunctionOption(null);
      onWorkCreated();

      // Redirect to the newly created project if it's a project (no parentId)
      if (!parentId && createdWork?.id) {
        router.push(`/${orgId}/projects/${createdWork.id}`);
      }
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys.getProjectItems(orgId),
      // });
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys.getProjectNavItems(orgId),
      // });
    } catch (error) {
      console.error("Error creating work:", error);

      // Show error toast
      toast.error("Failed to create work item", {
        duration: 3000,
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                {parentName
                  ? `Create Work Item for ${parentName}`
                  : "Create a New Project"}
              </span>
              {!parentId && titleActions && (
                <div className="mr-8 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Security
                  </span>
                  {titleActions}
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              {isNewProject
                ? "Create a project. Optionally link it to a product function now, or leave unassigned and link later."
                : "Add a new work item to your organization."}
            </DialogDescription>
          </DialogHeader>

          {parentId && parentName && (
            <div className="mb-4">
              <Badge variant="outline" className="px-2 py-1">
                Parent: {parentName}
              </Badge>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <DocumentationField control={form.control} />
                </div>
                <div className="sm:col-span-2">
                  <TextInputField
                    control={form.control}
                    name="name"
                    label="Name"
                    placeholder={
                      parentName
                        ? "Enter work item name"
                        : "Enter a project name"
                    }
                  />
                </div>

                {isNewProject ? (
                  <div className="sm:col-span-2">
                    <ProjectFunctionAssignmentField
                      value={functionFacetId}
                      preferredProductRootId={preferredProductRootId}
                      onChange={setFunctionFacetId}
                      onOptionChange={setLinkedFunctionOption}
                    />
                  </div>
                ) : null}

                <ComboboxFormField<WorkFormValues>
                  name="assignedTo"
                  label="Assigned To"
                  placeholder="Search for a person"
                  items={getPeopleOptions(people || [])}
                />
                <SelectField
                  control={form.control}
                  name="statusId"
                  label="Status"
                  placeholder="Select status"
                  options={workStatuses || []}
                  disabled={isWorkStatusesLoading}
                />

                <DatePickerField
                  control={form.control}
                  name="tentativeStartDate"
                  label="Planned Start Date"
                />

                <DatePickerField
                  control={form.control}
                  name="tentativeEndDate"
                  label="Planned End Date"
                />

                {parentName && (
                  <>
                    <SelectField
                      control={form.control}
                      name="workTypeId"
                      label="Work Type"
                      placeholder="Select work type"
                      options={workTypes || []}
                      disabled={isWorkTypesLoading}
                    />
                  </>
                )}

                <NumberField
                  control={form.control}
                  name="tentativeBudget"
                  label="Planned Budget"
                  placeholder="Enter planned budget"
                  formatAsCurrency={true}
                  currencyCode={settings?.currencyCode}
                />

                <div className="sm:col-span-2">
                  <TextareaField
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder="Enter work item description"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {isNewProject ? "Create Project" : "Create Work Item"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <AlertDialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close this
              dialog? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
